import { css, cx } from '@emotion/css';
import {
  formatSavedCard,
  useDeleteSavedCard,
  useSavedCards,
} from '../services/api/saved-cards';
import { Colors } from '../styles/colors';
import { p2Style } from '../styles/text';
import { Motion, Radius } from '../styles/tokens';

interface SavedCardSelectorProps {
  locationId: number;
  /** null = pay with a new card. */
  selectedCardId: number | null;
  onSelect: (cardId: number | null) => void;
  saveCard: boolean;
  onSaveCardChange: (saveCard: boolean) => void;
}

/**
 * Lets the buyer pay with a card they kept earlier, or enter a new one and
 * keep it for next time. We never see the card number — the gateway holds it
 * and gives us a token, so the list only shows what it tells us about the card.
 */
export const SavedCardSelector = (
  props: SavedCardSelectorProps,
): JSX.Element => {
  const { locationId, selectedCardId, onSelect, saveCard, onSaveCardChange } =
    props;
  const { data } = useSavedCards(locationId);
  const deleteCard = useDeleteSavedCard(locationId);

  const cards = data?.cards ?? [];
  const hasCards = cards.length > 0;

  // The gateway has no tokenization for this location's merchant — offering to
  // save a card would be a promise we cannot keep.
  if (!data?.enabled) {
    return <></>;
  }

  return (
    <div className={rootStyle}>
      {hasCards && (
        <>
          <label className={groupLabelStyle}>Pay with</label>
          <div
            className={optionGroupStyle}
            role="radiogroup"
            aria-label="Pay with"
          >
            {cards.map((card) => (
              <div
                key={card.id}
                className={cx(
                  optionRowStyle,
                  selectedCardId === card.id && optionRowSelectedStyle,
                )}
              >
                <label className={optionLabelStyle}>
                  <input
                    type="radio"
                    name="savedCard"
                    checked={selectedCardId === card.id}
                    onChange={() => onSelect(card.id)}
                  />
                  <span>{formatSavedCard(card)}</span>
                </label>
                <button
                  type="button"
                  className={removeButtonStyle}
                  disabled={deleteCard.isPending}
                  onClick={() => {
                    if (selectedCardId === card.id) {
                      onSelect(null);
                    }
                    deleteCard.mutate(card.id);
                  }}
                >
                  remove
                </button>
              </div>
            ))}
            <div
              className={cx(
                optionRowStyle,
                selectedCardId === null && optionRowSelectedStyle,
              )}
            >
              <label className={optionLabelStyle}>
                <input
                  type="radio"
                  name="savedCard"
                  checked={selectedCardId === null}
                  onChange={() => onSelect(null)}
                />
                <span>A new card</span>
              </label>
            </div>
          </div>
        </>
      )}
      {selectedCardId === null && (
        <label className={cx(optionLabelStyle, saveCardLabelStyle)}>
          <input
            type="checkbox"
            checked={saveCard}
            onChange={() => onSaveCardChange(!saveCard)}
          />
          <span>
            Save this card for next time, so you only pick the amount.
          </span>
        </label>
      )}
    </div>
  );
};

const rootStyle = css`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  margin-top: 1rem;
`;

const groupLabelStyle = css`
  ${p2Style}
  font-weight: bold;
`;

const optionGroupStyle = css`
  display: flex;
  flex-direction: column;
  border: 1px solid ${Colors.BORDER};
  border-radius: ${Radius.md};
  overflow: hidden;
  background: ${Colors.WHITE};

  > div + div {
    border-top: 1px solid ${Colors.BORDER};
  }
`;

const optionRowStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0 1rem 0 0;
  min-height: 3rem;
  transition: background-color ${Motion.fast};
`;

const optionRowSelectedStyle = css`
  background: ${Colors.CAPRI_BLUE_TINT};
`;

const optionLabelStyle = css`
  ${p2Style}
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  flex: 1;
  min-height: 3rem;
  padding: 0.5rem 1rem;
  input {
    width: 20px;
    height: 20px;
    margin: 0;
    flex: 0 0 auto;
    cursor: pointer;
    accent-color: ${Colors.CAPRI_BLUE};
  }
`;

const saveCardLabelStyle = css`
  padding: 0;
  min-height: 0;
  align-items: flex-start;
  input {
    margin-top: 0.125rem;
  }
`;

const removeButtonStyle = css`
  ${p2Style}
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: ${Radius.sm};
  cursor: pointer;
  font-weight: bold;
  font-size: 0.875rem;
  color: ${Colors.CAPRI_BLUE};
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      background: ${Colors.WHITE};
      text-decoration: underline;
      text-underline-offset: 0.2em;
    }
  }

  &:disabled {
    color: ${Colors.WINDOW_GRAY};
    cursor: not-allowed;
  }
`;
